import { parseWithZod } from "@conform-to/zod";
import { invariantResponse } from "@epic-web/invariant";
import { data, useFetcher, useFetchers } from "react-router";
import { z } from "zod";
import { setCookieConsent } from "#app/utils/cookie-consent.server.ts";
import { type Route } from "./+types/cookie-consent.ts";

export const handle = {
  getSitemapEntries: () => null,
};

const CookieConsentSchema = z.object({
  consent: z.enum(["accepted", "declined"]),
});

export async function action({ request }: Route.ActionArgs) {
  const formData = await request.formData();
  const submission = parseWithZod(formData, {
    schema: CookieConsentSchema,
  });

  invariantResponse(
    submission.status === "success",
    "Invalid consent value received",
  );

  const { consent } = submission.value;

  return data(
    { success: true },
    { headers: { "set-cookie": setCookieConsent(consent) } },
  );
}

export function useOptimisticCookieConsent() {
  const fetchers = useFetchers();
  const consentFetcher = fetchers.find(
    (f) => f.formAction === "/resources/cookie-consent",
  );

  if (consentFetcher?.formData) {
    const submission = parseWithZod(consentFetcher.formData, {
      schema: CookieConsentSchema,
    });

    if (submission.status === "success") {
      return submission.value.consent;
    }
  }
}

export function CookieConsentBanner({
  cookieConsent,
}: {
  cookieConsent: string | null;
}) {
  const fetcher = useFetcher<typeof action>();
  const optimisticConsent = useOptimisticCookieConsent();

  // Don't show if user already made a choice (or is in the process of choosing)
  if (cookieConsent || optimisticConsent) return null;

  return (
    <div className="fixed bottom-0 inset-x-0 z-50 p-4 sm:p-6">
      <div className="mx-auto max-w-2xl rounded-lg border border-gray-200 bg-white p-4 shadow-lg dark:border-border dark:bg-card sm:p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-muted-foreground">
            We use cookies for analytics to improve your experience.{" "}
            <a
              href="/privacy"
              className="underline underline-offset-4 hover:text-foreground"
            >
              Privacy Policy
            </a>
          </p>
          <div className="flex shrink-0 gap-2">
            <fetcher.Form
              method="POST"
              action="/resources/cookie-consent"
            >
              <input type="hidden" name="consent" value="declined" />
              <button
                type="submit"
                className="rounded-md border border-gray-200 bg-transparent px-4 py-2 text-sm font-medium hover:bg-gray-100 dark:border-border dark:hover:bg-accent"
              >
                Decline
              </button>
            </fetcher.Form>
            <fetcher.Form
              method="POST"
              action="/resources/cookie-consent"
            >
              <input type="hidden" name="consent" value="accepted" />
              <button
                type="submit"
                className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/80"
              >
                Accept
              </button>
            </fetcher.Form>
          </div>
        </div>
      </div>
    </div>
  );
}
